import { useT } from "@transifex/react";
import { useMemo, useState } from "react";
import moment from "moment";
import type { AeaExerciseModel, TeacherTestDetails } from "p6m-p6u";
import BasePopupWrapper, { TitleColorVariants } from "../../../basic/popups/BasePopupWrapper";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import Checkbox from "../../../basic/checkbox/Checkbox";
import {
    checkboxCheckedStyles,
    ClassActivityCategory,
    classActivityColors,
} from "../../../constants/ClassActivityConstants";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { useRTContext } from "../../../context/RTContext";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import { useSchoolClassOperations, type ReassignData } from "../../../hooks/useSchoolClassOperations";
import { ShareModes } from "../../../enums/shareFlow";
import type { SchoolClassVisibilities } from "../../../enums/schoolClasses";
import { isPrivateSchoolClass } from "../../../utils/schoolClassUtils";
import { IconPosition } from "../../teacherArea/AreaButton";
import { FormattedText } from "../../teacherArea/FormattedText";
import { toggleInSet } from "../../../helpers/SetHelper";

interface SelectExistingActivityPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onBack: () => void;
    schoolClassId: string;
    schoolClassName: string;
    classVisibility: SchoolClassVisibilities;
    onAssignComplete: (count: number) => void;
    vocabTrainings: AeaExerciseModel[];
    reloadVocabTrainings: () => Promise<unknown>;
}

interface SelectableActivity {
    id: string;
    category: ClassActivityCategory;
    title: string;
    lastModified: string;
    sortTimestamp: number;
}

const INITIAL_ITEMS_SHOWN = 4;

const _formatTestDate = (test: TeacherTestDetails): string => {
    if (test.updatedDate) {
        return moment.unix((test.updatedDate as number) / 1000).format("DD.MM.YYYY");
    }
    if (test.createdDate) {
        return moment.unix((test.createdDate as number) / 1000).format("DD.MM.YYYY");
    }
    return "";
};

const SelectExistingActivityPopup = (props: SelectExistingActivityPopupProps) => {
    const t = useT();
    const { setIsDataBeingLoaded } = useGeneralContext();
    const { userTests, studentTestContentWrapper, loadUserTests, openWarningModal } = useTestCreationContext();
    const { updateVocabTrainingSchoolClass } = useRTContext();
    const { handleTestSchoolClassChange } = useSchoolClassOperations();

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [showAll, setShowAll] = useState(false);
    const [isAssigning, setIsAssigning] = useState(false);

    const t_title = t("Add activity to class", { _tags: "SchoolClasses, SelectActivity" });
    const t_question = t('Which activity/activities should be assigned to <b>"{className}"</b>?', {
        _tags: "SchoolClasses, SelectActivity",
        className: props.schoolClassName,
    });
    const t_infoNote = t("Activities can only be assigned to one class.", {
        _tags: "SchoolClasses, SelectActivity",
    });
    const t_showMore = t("show more", { _tags: "SchoolClasses, SelectActivity" });
    const t_allAssigned = t("All activities are already assigned to classes.", {
        _tags: "SchoolClasses, SelectActivity",
    });
    const t_back = t("Back", { _tags: "SchoolClasses, SelectActivity" });
    const t_add = t("Add", { _tags: "SchoolClasses, SelectActivity" });
    const t_lastModified = t("Last modified:", { _tags: "SchoolClasses, SelectActivity" });
    const t_testLabel = t("PRACTICE & TEST", { _tags: "SchoolClasses, SelectActivity" });
    const t_vocabLabel = t("VOCABULARY TRAINING", { _tags: "SchoolClasses, SelectActivity" });
    const t_errorAssigning = t("There was a problem assigning the activities. Please try again.", {
        _tags: "SchoolClasses, SelectActivity",
    });
    const t_linkChanges = t("The link to this activity will change due to the assignment.", {
        _tags: "SchoolClasses, SelectActivity",
    });

    const unassignedActivities = useMemo((): SelectableActivity[] => {
        const testActivities: SelectableActivity[] = userTests
            .filter((test: TeacherTestDetails) => {
                if (test.groupId) return false;
                // Exclude only legacy tests (shared before the share-mode split): they carry a share
                // record without a shareMode.
                if (!studentTestContentWrapper.hasOwnProperty(test.id ?? "")) return true;
                const shareMode = studentTestContentWrapper[test.id ?? ""]?.shareMode ?? test.shareMode ?? null;
                return shareMode !== null;
            })
            .map((test: TeacherTestDetails) => {
                const rawTimestamp = (test.updatedDate as number) || (test.createdDate as number) || 0;
                return {
                    id: test.id ?? "",
                    category: ClassActivityCategory.TEST,
                    title: test.name ?? "",
                    lastModified: _formatTestDate(test),
                    sortTimestamp: rawTimestamp,
                };
            });

        const vocabActivities: SelectableActivity[] = props.vocabTrainings
            .filter((training: AeaExerciseModel) => !training.groupId)
            .map((training: AeaExerciseModel) => ({
                id: training.id ?? "",
                category: ClassActivityCategory.VOCAB_TRAINING,
                title: training.testName ?? "",
                lastModified: training.createdDate ? moment(training.createdDate).format("DD.MM.YYYY") : "",
                sortTimestamp: training.createdDate ? moment(training.createdDate).valueOf() : 0,
            }));

        return [...testActivities, ...vocabActivities].sort((a, b) => b.sortTimestamp - a.sortTimestamp);
    }, [userTests, props.vocabTrainings, studentTestContentWrapper]);

    const visibleActivities = showAll ? unassignedActivities : unassignedActivities.slice(0, INITIAL_ITEMS_SHOWN);

    const toggleSelection = (id: string) => {
        setSelectedIds((prev) => toggleInSet(prev, id));
    };

    const isSharedActivity = (activity: SelectableActivity): boolean =>
        activity.category === ClassActivityCategory.TEST && !!studentTestContentWrapper[activity.id];

    // A shared activity cannot just take a new groupId: the student-test record carries its own, and
    // leaving it behind unlinks the activity from the class for students. Reassignment therefore goes
    // through copy + re-share + delete. Mirrors TestCardExpandedContent — a shared test moving into a
    // public class drops sharing, since answers and evaluations cannot survive the move.
    const _getReassignData = (testId: string): ReassignData | undefined => {
        const sharedContent = studentTestContentWrapper[testId];
        if (!sharedContent) return undefined;

        const isSharedTest = sharedContent.shareMode === ShareModes.TEST;
        const targetIsPrivate = isPrivateSchoolClass(props.classVisibility);
        return { sharedContent: isSharedTest && !targetIsPrivate ? null : sharedContent };
    };

    const handleAssign = async () => {
        setIsAssigning(true);
        setIsDataBeingLoaded(true);

        try {
            const selectedActivities = unassignedActivities.filter((a) => selectedIds.has(a.id));

            const results = await Promise.all(
                selectedActivities.map((activity) => {
                    if (activity.category === ClassActivityCategory.TEST) {
                        return handleTestSchoolClassChange(
                            activity.id,
                            props.schoolClassId,
                            _getReassignData(activity.id)
                        );
                    }
                    return updateVocabTrainingSchoolClass(activity.id, props.schoolClassId);
                })
            );

            await Promise.all([loadUserTests(true), props.reloadVocabTrainings()]);

            setSelectedIds(new Set());
            setShowAll(false);
            props.onClose();

            // A failed reassignment resolves to false rather than throwing, and has already surfaced its
            // own error — so report only the activities that actually landed.
            const assignedCount = results.filter(Boolean).length;
            if (assignedCount === 0) return;
            props.onAssignComplete(assignedCount);
        } catch (e) {
            props.onClose();
            openWarningModal(t_errorAssigning);
        } finally {
            setIsDataBeingLoaded(false);
            setIsAssigning(false);
        }
    };

    const _getSelectedBackground = (category: ClassActivityCategory): string => {
        if (category === ClassActivityCategory.TEST) return "bg-p6-orange-light-pastel";
        return "bg-forest-green-pastel";
    };

    const renderActivityItem = (activity: SelectableActivity) => {
        const isSelected = selectedIds.has(activity.id);
        const colors = classActivityColors[activity.category];
        const label = activity.category === ClassActivityCategory.TEST ? t_testLabel : t_vocabLabel;

        const selectionStyles = isSelected
            ? `${colors.border} ${_getSelectedBackground(activity.category)}`
            : "border-transparent ring-1 ring-p6-grey-light bg-white";

        return (
            <button
                key={activity.id}
                type="button"
                data-testid={`select-existing-activity-${activity.id}`}
                onClick={() => toggleSelection(activity.id)}
                className={`w-full flex gap-m items-center p-s rounded-medium cursor-pointer border-2 border-solid text-left transition-colors ${selectionStyles}`}
            >
                <div className="shrink-0">
                    <Checkbox
                        isChecked={isSelected}
                        checkedClassName={checkboxCheckedStyles[activity.category]}
                    />
                </div>
                <div className="flex flex-col gap-xxs flex-1 min-w-0 overflow-hidden">
                    <span className={`text-l font-semibold ${colors.text}`}>{label}</span>
                    <span className="text-xl font-semibold text-p6-grey-dark break-all line-clamp-2">
                        {activity.title}
                    </span>
                    <span className="text-l text-p6-grey-mid break-all">
                        {t_lastModified} {activity.lastModified}
                    </span>
                </div>
            </button>
        );
    };

    const hasActivities = unassignedActivities.length > 0;
    // Same reasoning as the class tag's hint: the copy + delete reassignment issues a new id.
    const hasSharedSelection = unassignedActivities.some((a) => selectedIds.has(a.id) && isSharedActivity(a));

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={props.onClose}
            title={t_title}
            titleColor={TitleColorVariants.NEUTRAL}
            secondaryButton={{
                text: t_back,
                icon: "chevron-left",
                iconPosition: IconPosition.LEFT,
                onClick: () => {
                    setSelectedIds(new Set());
                    setShowAll(false);
                    props.onBack();
                },
                className: "min-w-[180px] mr-auto",
            }}
            primaryButton={{
                text: t_add,
                onClick: handleAssign,
                disabled: selectedIds.size === 0 || isAssigning,
                className: "min-w-[180px]",
            }}
        >
            <div className="flex flex-col gap-m">
                <div className="flex flex-col gap-xxs">
                    <FormattedText
                        rawText={t_question}
                        textColor="text-l text-p6-grey-dark"
                    />
                    <div className="flex gap-xxs items-center">
                        <PhaseSixIcon
                            name="info"
                            className="text-xl text-p6-grey-dark shrink-0"
                        />
                        <span className="text-l text-p6-grey-dark">{t_infoNote}</span>
                    </div>
                    {hasSharedSelection && (
                        <div className="flex gap-xxs items-start">
                            <PhaseSixIcon
                                name="info"
                                className="text-xl text-p6-grey-dark shrink-0"
                            />
                            <span className="text-l text-p6-grey-dark">{t_linkChanges}</span>
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-s">
                    {hasActivities ? (
                        <>
                            {visibleActivities.map(renderActivityItem)}
                            {!showAll && unassignedActivities.length > INITIAL_ITEMS_SHOWN && (
                                <button
                                    type="button"
                                    data-testid="select-existing-activity-show-more"
                                    onClick={() => setShowAll(true)}
                                    className="flex items-center justify-center gap-xxs w-full cursor-pointer bg-transparent border-none p-no"
                                >
                                    <span className="text-l text-p6-grey-dark">{t_showMore}</span>
                                    <PhaseSixIcon
                                        name="chevron-down"
                                        className="text-l text-p6-grey-dark"
                                    />
                                </button>
                            )}
                        </>
                    ) : (
                        <div className="w-full border-2 border-p6-grey-light border-solid rounded-medium p-s flex items-center justify-center">
                            <span className="text-l text-p6-grey-mid">{t_allAssigned}</span>
                        </div>
                    )}
                </div>
            </div>
        </BasePopupWrapper>
    );
};

export default SelectExistingActivityPopup;
